// First-touch attribution capture.
//
// Runs once on the first page load in a browser session and writes the
// captured referrer + utm parameters to localStorage under "firstTouch".
// Subsequent visits do NOT overwrite: first touch wins. Signup.tsx reads
// this alongside URL params so we can tell direct/Google/Meta/etc. even
// when the user navigates around before signing up.

interface FirstTouch {
  source: string;
  referrer: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  landingPath?: string;
  capturedAt: string;
}

const STORAGE_KEY = "firstTouch";

function categorizeReferrer(refUrl: string, ownHost: string): string | undefined {
  try {
    if (!refUrl) return undefined;
    const u = new URL(refUrl);
    const h = u.hostname.toLowerCase();
    if (h === ownHost) return undefined;
    if (h.includes("google.")) return "google";
    if (h.includes("bing.") || h.includes("duckduckgo.")) return "search";
    if (h.includes("facebook.") || h.includes("instagram.") || h.includes("m.me") || h.includes("fb.")) return "meta";
    if (h.includes("wa.me") || h.includes("whatsapp.")) return "whatsapp";
    if (h.includes("t.co") || h.includes("twitter.") || h.includes("x.com")) return "twitter";
    if (h.includes("tiktok.")) return "tiktok";
    return h;
  } catch {
    return undefined;
  }
}

export function captureFirstTouch(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(STORAGE_KEY)) return;
    const sp = new URLSearchParams(window.location.search);
    const utmSource = sp.get("utm_source") || undefined;
    const utmMedium = sp.get("utm_medium") || undefined;
    const utmCampaign = sp.get("utm_campaign") || undefined;
    // utm_content and utm_term separate one creative from another inside a campaign, which is the
    // whole point of running nine images. fbclid is the click id: Meta's Pixel normally turns it
    // into the _fbc cookie, but that only happens if the Pixel actually runs on the page the click
    // landed on. Capture it ourselves so a blocked or late-loading Pixel does not lose the click.
    const utmContent = sp.get("utm_content") || undefined;
    const utmTerm = sp.get("utm_term") || undefined;
    const fbclid = sp.get("fbclid") || undefined;
    const ref = sp.get("ref") || undefined;

    const referrer = document.referrer || "";
    const derived = categorizeReferrer(referrer, window.location.hostname);
    // An fbclid is proof of a Meta click even when the ad was built without utm parameters.
    const source = ref || utmSource || derived || (fbclid ? "meta" : undefined) || "direct";

    const payload: FirstTouch = {
      source,
      referrer,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      fbclid,
      // Which paid page they actually landed on, so /pep-prep can be measured against /signup.
      landingPath: window.location.pathname,
      capturedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Fail silently: attribution is a nice-to-have, not a blocker.
  }
}

/** What the register endpoint accepts. Keys match the insertUserSchema fields in shared/schema.ts. */
export interface ResolvedAttribution {
  signupSource?: string;
  signupUtmSource?: string;
  signupUtmMedium?: string;
  signupUtmCampaign?: string;
  signupUtmContent?: string;
  signupUtmTerm?: string;
  signupFbclid?: string;
  signupLandingPath?: string;
}

/**
 * Work out what to attribute a registration to, for whichever page is doing the registering.
 *
 * The order is deliberate. Parameters on the current URL win, because they describe the click that
 * is converting right now. Behind them sits the stored first touch, so a parent who lands on an ad,
 * wanders through the site and signs up two pages later is still credited to the ad. Behind that,
 * the referrer is categorised, which is the only thing that catches an organic Facebook post. Only
 * when all of those are empty does it fall through to "direct".
 *
 * Signup.tsx and the paid landing pages both call this. It lives here rather than in either page
 * so the two cannot drift apart and start reporting different sources for the same visit.
 */
export function resolveAttribution(): ResolvedAttribution {
  if (typeof window === "undefined") return {};
  const sp = new URLSearchParams(window.location.search);
  const pick = (k: string) => sp.get(k) || undefined;

  let stored: Partial<FirstTouch> = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as FirstTouch;
  } catch {
    // A blocked or full localStorage must not stop someone registering.
  }

  const ref = pick("ref");
  const utmSource = pick("utm_source");
  const fbclid = pick("fbclid") || stored.fbclid;
  const derived =
    categorizeReferrer(document.referrer || "", window.location.hostname) ||
    (stored.referrer ? categorizeReferrer(stored.referrer, window.location.hostname) : undefined);

  return {
    signupSource:
      ref || utmSource || stored.source || derived || (fbclid ? "meta" : undefined) || "direct",
    signupUtmSource: utmSource || stored.utmSource,
    signupUtmMedium: pick("utm_medium") || stored.utmMedium,
    signupUtmCampaign: pick("utm_campaign") || stored.utmCampaign,
    signupUtmContent: pick("utm_content") || stored.utmContent,
    signupUtmTerm: pick("utm_term") || stored.utmTerm,
    signupFbclid: fbclid,
    signupLandingPath: stored.landingPath || window.location.pathname,
  };
}
